/**
 * Manueller Abgleich einer mehrdeutigen Bindung.
 *
 * Warum es diesen Dialog überhaupt gibt: ein Vendor-Create, dessen Ausgang wir nicht kennen, sperrt
 * die Bindung — und damit **jeden** weiteren Push des Objekts. Bis hierher gab es dafür keine
 * Fläche, auch nicht für den Plattform-Admin; der Weg heraus führte über Bruno.
 *
 * Warum es kein „Nochmal versuchen" gibt: ein zweiter POST auf einen möglicherweise geglückten
 * Create legte die Ressource ein zweites Mal an (§6.102). Die Frage, die nur ein Mensch beantworten
 * kann, ist deshalb nicht „wiederholen?", sondern „existiert sie dort?" — und genau die stellt
 * dieser Dialog. Vendor-Beziehungen sind unveränderliche Identität, keine reparierbaren Attribute.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
} from '@/shared/components';
import type { ChannexReconcileAction, ChannexUnitResource } from '@/shared/api';

export interface ReconcileTarget {
  scope: 'parent' | 'unit';
  bindingId: string;
  /** Objektname beim Parent, Apartmentname bei der Unit — damit klar ist, was abgeglichen wird. */
  label: string;
  resource?: ChannexUnitResource | null;
}

const RESOURCE_LABEL: Record<ChannexUnitResource, string> = {
  RoomType: 'Zimmertyp',
  RatePlan: 'Ratenplan',
};

interface Props {
  target: ReconcileTarget;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (action: ChannexReconcileAction, remoteId?: string) => void | Promise<void>;
}

export function ReconcileBindingDialog({ target, pending, onClose, onConfirm }: Props) {
  const [action, setAction] = useState<ChannexReconcileAction>('attach');
  const [remoteId, setRemoteId] = useState('');

  useEffect(() => {
    setAction('attach');
    setRemoteId('');
  }, [target.bindingId]);

  const what =
    target.scope === 'parent'
      ? 'das Objekt'
      : `den ${RESOURCE_LABEL[target.resource ?? 'RoomType']}`;
  const confirmDisabled = pending || (action === 'attach' && remoteId.trim().length === 0);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Abgleich für „{target.label}“</DialogTitle>
          <DialogDescription>
            Der letzte Versuch, {what} bei Channex anzulegen, hat kein eindeutiges Ergebnis
            geliefert. Solange das offen ist, bleibt die Bindung gesperrt und kein weiterer Push des
            Objekts geht raus. Ein erneuter Versuch ist bewusst nicht möglich — er könnte die
            Ressource ein zweites Mal anlegen. Schau bei Channex nach und sag hier, was du siehst.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <fieldset className="space-y-3">
            <label className="border-line bg-card-alt/40 flex cursor-pointer items-start gap-3 rounded-md border p-3">
              <input
                type="radio"
                name="reconcile-action"
                className="mt-1"
                checked={action === 'attach'}
                onChange={() => setAction('attach')}
                disabled={pending}
              />
              <span className="text-sm">
                <span className="text-ink font-semibold">Sie existiert bei Channex.</span>
                <span className="text-ink-soft block">
                  Wir übernehmen ihre Kennung — es wird nichts Neues angelegt.
                </span>
              </span>
            </label>
            {action === 'attach' && (
              <div className="pl-8">
                <FormField label="Channex-Kennung" required>
                  <Input
                    value={remoteId}
                    disabled={pending}
                    autoComplete="off"
                    aria-label="Channex-Kennung der bestehenden Ressource"
                    onChange={(event) => setRemoteId(event.target.value)}
                    placeholder="z. B. 7f3a…"
                  />
                </FormField>
              </div>
            )}
            <label className="border-line bg-card-alt/40 flex cursor-pointer items-start gap-3 rounded-md border p-3">
              <input
                type="radio"
                name="reconcile-action"
                className="mt-1"
                checked={action === 'confirm-absent'}
                onChange={() => setAction('confirm-absent')}
                disabled={pending}
              />
              <span className="text-sm">
                <span className="text-ink font-semibold">Dort existiert nichts.</span>
                <span className="text-ink-soft block">
                  Die Sperre fällt, und die Veröffentlichung legt sie neu an.
                </span>
              </span>
            </label>
          </fieldset>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={pending}
            disabled={confirmDisabled}
            onClick={() => void onConfirm(action, remoteId.trim() || undefined)}
          >
            Abgleich bestätigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
